import { Controller } from "@hotwired/stimulus"

const focusableSelector = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",")

export default class extends Controller {
  static targets = ["container", "dialog", "backdrop"]
  static values = {
    open: Boolean,
    esc: { type: Boolean, default: true },
    dismissible: { type: Boolean, default: true },
    closeOnConfirm: { type: Boolean, default: true }
  }

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.trapFocus = this.trapFocus.bind(this)

    if (this.hasOpenValue && this.openValue) {
      this.syncState()
    }
  }

  disconnect() {
    this.removeDocumentListeners()
  }

  open(event) {
    if (event) event.preventDefault()
    this.openValue = true
  }

  close(event) {
    if (event) event.preventDefault()
    this.openValue = false
  }

  confirm(event) {
    if (event) event.preventDefault()

    const trigger = event ? event.currentTarget : null
    this.dispatch("confirm", { detail: { trigger } })

    if (this.closeOnConfirmValue) {
      this.openValue = false
    }
  }

  closeFromBackdrop(event) {
    if (!this.dismissibleValue) return
    if (event && event.target === this.backdropTarget) {
      this.close(event)
    }
  }

  openValueChanged() {
    this.syncState()
  }

  syncState() {
    if (this.openValue) {
      this.show()
    } else {
      this.hide()
    }
  }

  show() {
    this.containerTarget.hidden = false
    this.containerTarget.classList.remove("hidden")
    this.containerTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("overflow-hidden")

    this.addDocumentListeners()
    this.focusInitialElement()
  }

  hide() {
    this.containerTarget.hidden = true
    this.containerTarget.classList.add("hidden")
    this.containerTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("overflow-hidden")

    this.removeDocumentListeners()
    this.restoreFocus()
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.escValue) {
      event.preventDefault()
      this.close(event)
      return
    }

    if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  trapFocus(event) {
    const focusable = this.focusableElements()

    if (focusable.length === 0) {
      event.preventDefault()
      this.dialogTarget.focus({ preventScroll: true })
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey) {
      if (active === first) {
        event.preventDefault()
        last.focus()
      }
    } else if (active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusInitialElement() {
    this.previouslyFocusedElement = document.activeElement

    const focusable = this.focusableElements()
    const target = focusable[0] || this.dialogTarget

    if (!this.dialogTarget.hasAttribute("tabindex")) {
      this.dialogTarget.setAttribute("tabindex", "-1")
    }

    target.focus({ preventScroll: true })
  }

  restoreFocus() {
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === "function") {
      this.previouslyFocusedElement.focus({ preventScroll: true })
    }
    this.previouslyFocusedElement = null
  }

  focusableElements() {
    return Array.from(this.dialogTarget.querySelectorAll(focusableSelector)).filter((element) => {
      return this.isVisible(element)
    })
  }

  isVisible(element) {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
  }

  addDocumentListeners() {
    document.addEventListener("keydown", this.handleKeydown)
  }

  removeDocumentListeners() {
    document.removeEventListener("keydown", this.handleKeydown)
  }
}
